import { applyCleValidationErrorsToFormik } from "@/api/cle-api/errors";
import type {
  LegalAidCategory,
  LegalAidEvidenceModel,
  UpdateLegalAidRequest,
} from "@/api/cle-api";
import Button from "@/components/bricks/button/Button";
import { CleApiErrorAlert } from "@/components/cle-api/CleApiErrorAlert";
import ValidationErrorMessage from "@/components/formcontrols/ValidationErrorMessage";
import EditIcon from "@/components/icons/EditIcon";
import Modal, {
  ModalContext,
  ModalProvider,
  ModalTrigger,
} from "@/components/Modal/Modal";
import Spinner from "@/components/spinner/Spinner";
import { StickyFooter } from "@/components/stickyFooter/modalStickyFooter";
import { useUpdateLegalAid } from "@/hooks/cle-hooks";
import { Form, FormikProvider, useFormik } from "formik";
import { useContext, useMemo } from "react";
import * as Yup from "yup";
import { YupNumberOfLessonsNullable } from "../../../util/yup";

import { formatLegalAidCategory } from "@/api/cle-api/models/legalAid";

export interface LegalAidEditFormValues {
  category: LegalAidCategory;
  numberOfLessons: string;
}

const validationSchema = Yup.object({
  category: Yup.string()
    .oneOf(["advokatvagt", "retshjaelp"])
    .required("Vælg type"),
  numberOfLessons: YupNumberOfLessonsNullable(),
});

export const legalAidToFormValues = (
  legalAid: LegalAidEvidenceModel,
): LegalAidEditFormValues => ({
  category: legalAid.category,
  numberOfLessons:
    legalAid.numberOfLessons != null ? String(legalAid.numberOfLessons) : "",
});

export const formValuesToUpdateRequest = (
  values: LegalAidEditFormValues,
  initialValues: LegalAidEditFormValues,
): UpdateLegalAidRequest => {
  const body: UpdateLegalAidRequest = {};

  if (values.category !== initialValues.category) {
    body.category = values.category;
  }

  const numberOfLessons =
    values.numberOfLessons === "" ? undefined : Number(values.numberOfLessons);
  const initialNumberOfLessons =
    initialValues.numberOfLessons === ""
      ? undefined
      : Number(initialValues.numberOfLessons);
  if (numberOfLessons !== initialNumberOfLessons) {
    body.number_of_lessons = numberOfLessons;
  }

  return body;
};

interface LegalAidEditFormProps {
  legalAid: LegalAidEvidenceModel;
  onSave: (updated: LegalAidEvidenceModel) => void;
}

const LegalAidEditForm = ({ legalAid, onSave }: LegalAidEditFormProps) => {
  const { setModalOpen } = useContext(ModalContext);
  const {
    mutate: updateLegalAid,
    isPending,
    isError,
    error,
    reset: resetMutation,
  } = useUpdateLegalAid();

  const initialFormValues = useMemo(
    () => legalAidToFormValues(legalAid),
    [legalAid],
  );

  const formik = useFormik<LegalAidEditFormValues>({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const body = formValuesToUpdateRequest(values, initialFormValues);
      if (Object.keys(body).length === 0) return;

      updateLegalAid(
        {
          legalAidId: legalAid.id,
          userId: legalAid.ownerUserId,
          body,
        },
        {
          onSuccess: (updated) => {
            onSave(updated);
            resetMutation();
            setModalOpen(false);
          },
          onError: (err) => {
            applyCleValidationErrorsToFormik(err, formik.setFieldError);
          },
        },
      );
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    resetMutation();
    setModalOpen(false);
  };

  const numberOfLessonsInvalid =
    formik.touched.numberOfLessons && formik.errors.numberOfLessons;

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <div className="c-modal-container">
          <div className="c-modal-container-inner">
            <div className="rounded border bg-light p-3 mb-2">
              <div className="text-muted small mb-1">Advokatvagt/retshjælp</div>
              <div className="fw-semibold text-break">
                {legalAid.fileName ?? "Ukendt fil"}
              </div>
            </div>

            <div className="row g-4">
              <div className="col-sm-6">
                <label className="form-label" htmlFor="category">
                  Type
                </label>
                <select
                  className="form-select"
                  id="category"
                  name="category"
                  value={formik.values.category}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                >
                  <option value="advokatvagt">
                    {formatLegalAidCategory("advokatvagt")}
                  </option>
                  <option value="retshjaelp">
                    {formatLegalAidCategory("retshjaelp")}
                  </option>
                </select>
              </div>

              <div className="col-sm-6">
                <label className="form-label" htmlFor="numberOfLessons">
                  Antal lektioner
                </label>
                <div className="input-group w-100">
                  <input
                    autoComplete="off"
                    className={` form-control${
                      numberOfLessonsInvalid ? " is-invalid" : ""
                    }`}
                    id="numberOfLessons"
                    min="0"
                    name="numberOfLessons"
                    step="any"
                    type="number"
                    value={formik.values.numberOfLessons}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                  />
                  <span className="input-group-text">lektioner</span>
                </div>
                <div className="form-text">Max 18 lektioner pr. periode.</div>
                <ValidationErrorMessage
                  touched={formik.touched.numberOfLessons}
                  errorMessage={formik.errors.numberOfLessons}
                />
              </div>
            </div>
          </div>
          {isError && (
            <div className="c-modal-container-inner">
              <CleApiErrorAlert
                error={error}
                fallback="Kunne ikke gemme opdatering."
              />
            </div>
          )}
          <StickyFooter className="border-top">
            <Button color="secondary" type="button" onClick={handleCancel}>
              Annuller
            </Button>
            <div className="d-flex gap-2 align-items-center">
              {isPending && <Spinner size={24} />}
              <Button
                color="primary"
                disabled={isPending || !formik.dirty}
                type="submit"
              >
                Gem
              </Button>
            </div>
          </StickyFooter>
        </div>
      </Form>
    </FormikProvider>
  );
};

interface LegalAidEditModalProps {
  legalAid: LegalAidEvidenceModel;
  onSave: (updated: LegalAidEvidenceModel) => void;
}

export const LegalAidEditModal = ({
  legalAid,
  onSave,
}: LegalAidEditModalProps) => (
  <ModalProvider>
    <ModalTrigger className="btn btn-primary btn--transparent has-icon px-2 edit-trigger">
      Redigér
      <EditIcon height={16} />
    </ModalTrigger>
    <Modal title="Redigér advokatvagt/retshjælp">
      <LegalAidEditForm legalAid={legalAid} onSave={onSave} />
    </Modal>
  </ModalProvider>
);
